import { useCallback, useState } from "react";
import type { ChangeEvent } from "react";

import toast from "react-hot-toast";

import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";

import SettingActions from "../SettingActions";

const InitialProfileSetup = () => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState(user?.profile?.firstName);
  const [lastName, setLastName] = useState(user?.profile?.lastName);

  const saveProfile = useCallback(() => {
    if (!user) return;
    setSaving(true);
    const profile = {
      ...user.profile,
      firstName: firstName ?? "",
      lastName: lastName ?? "",
    };
    api.users.update(
      user._id,
      { profile },
      (newUser) => {
        setSaving(false);
        dispatch(
          actions.user.receiveUser({ ...user, profile: newUser.profile }),
        );
      },
      (err) => {
        toast.error(err.message);
        setSaving(false);
      },
    );
  }, [firstName, lastName, user, dispatch]);

  return (
    <>
      <H3>
        You are about to join {user?.ownerOrganisation?.name}!
        <Subheader>
          {" "}
          We just need to know a few more details about you. To continue, please
          let others know who you are.
        </Subheader>
      </H3>
      <Grid columns={2} mobile={1}>
        <GridColumn>
          <FormField>
            <FormLabel>First name</FormLabel>
            <Input
              fluid
              placeholder="First name"
              value={firstName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFirstName(e.target.value)
              }
              autoComplete="given-name"
            />
          </FormField>
        </GridColumn>
        <GridColumn>
          <FormField>
            <FormLabel>Last name</FormLabel>
            <Input
              fluid
              placeholder="Last name"
              value={lastName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLastName(e.target.value)
              }
              autoComplete="family-name"
            />
          </FormField>
        </GridColumn>
      </Grid>
      <Divider hidden />
      <SettingActions>
        <Button
          onClick={saveProfile}
          loading={saving}
          disabled={!firstName || !lastName}
          color="primary"
        >
          Save profile
        </Button>
      </SettingActions>
    </>
  );
};

export default InitialProfileSetup;
